import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { h } from '../lib/format';

/**
 * Chart conventions (figures defined in docs/KPI.md):
 * - one blue ramp, read as "more revenue relevant = darker": billable = strong, included in the fee = mid,
 *   other hours = soft (validated as ordinal ramp in light and dark mode); capacity = ink line
 * - columns at most 24 px wide, 4 px rounded data end, square at the baseline,
 *   2 px surface gap between stacked parts and between neighbouring columns
 * - week state marks carry an icon and a label, never colour alone (over = amber up, under = red down)
 * - hairline grid, labels in text colours, legend always visible
 * - tooltip per column on hover and keyboard focus, and a table view of the same numbers
 */

export type Kind = 'strong' | 'mid' | 'soft' | 'line';

const FILL: Record<Exclude<Kind, 'line'>, string> = { strong: 'bg-viz-strong', mid: 'bg-viz-mid', soft: 'bg-viz-soft' };

export interface Series {
  key: string;
  label: ReactNode;
}

export function Legend({ items }: { items: { kind: Kind | 'over' | 'under'; label: ReactNode }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {items.map((i, n) => (
        <span key={n} className="flex items-center gap-1.5">
          {i.kind === 'line' ? (
            <span className="inline-block h-0.5 w-3.5 rounded bg-ink" />
          ) : i.kind === 'over' ? (
            <ChevronUp className="size-3.5 text-warn" aria-hidden />
          ) : i.kind === 'under' ? (
            <ChevronDown className="size-3.5 text-bad" aria-hidden />
          ) : (
            <span className={clsx('inline-block size-2.5 rounded-[3px]', FILL[i.kind])} />
          )}
          {i.label}
        </span>
      ))}
    </div>
  );
}

export function DataTable({ head, columns, rows, format = (v) => h(v) }: { head: ReactNode; columns: Series[]; rows: { label: string; values: Record<string, number | string> }[]; format?: (v: number) => string }) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-line">
        <tr>
          <th className="py-1 text-left text-xs font-medium text-graphite">{head}</th>
          {columns.map((c) => (
            <th key={c.key} className="py-1 text-right text-xs font-medium text-graphite">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-b border-line/60">
            <td className="py-1">{r.label}</td>
            {columns.map((c) => (
              <td key={c.key} className="num py-1 text-right">
                {typeof r.values[c.key] === 'number' ? format(r.values[c.key] as number) : r.values[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Frame({ legend, table, children }: { legend: ReactNode; table: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  return (
    <figure className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-graphite">
        {legend}
        <button type="button" className="rounded px-1.5 py-0.5 text-xs font-medium text-accent hover:bg-accent-soft" onClick={() => setAsTable((x) => !x)}>
          {asTable ? t('kpi.showChart') : t('kpi.showTable')}
        </button>
      </div>
      {asTable ? table : children}
    </figure>
  );
}

function niceScale(maxMinutes: number): { top: number; step: number } {
  const hours = Math.max(1, maxMinutes / 60);
  const step = hours > 400 ? 100 : hours > 150 ? 50 : hours > 60 ? 20 : hours > 24 ? 10 : hours > 8 ? 4 : 2;
  return { top: Math.ceil(hours / step) * step * 60, step: step * 60 };
}

function TooltipRow({ kind, value, label }: { kind: Kind; value: number; label: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className={clsx('inline-block h-0.5 w-3 rounded', kind === 'line' ? 'bg-ink' : FILL[kind])} />
      <strong className="num font-semibold text-ink">{h(value)} h</strong>
      <span className="text-graphite">{label}</span>
    </div>
  );
}

export interface WeekDatum {
  /** Full label for tooltip and table, e.g. "KW 36" or "Mo 21.09.". */
  label: string;
  /** Short axis label, e.g. "36" or "Mo". */
  axis: string;
  booked: number;
  billable: number;
  capacity: number;
  /** Time off in lieu inside the target (Freizeitausgleich): not expected to be booked. */
  comp?: number;
  /** Finished week compared with capacity (optional). */
  state?: 'under' | 'over' | 'ok' | null;
  /** Extra tooltip line, e.g. holiday name. */
  note?: string;
  /** Highlight (today / current week). */
  current?: boolean;
}

/** Columns: billable part (strong) and the rest of the booked time (soft), capacity as a line, optional week state marks. */
export function WeekBars({ data, height = 192, head, showCapacity = true }: { data: WeekDatum[]; height?: number; head?: ReactNode; showCapacity?: boolean }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  if (!data.length) return null;
  const { top, step } = niceScale(Math.max(...data.map((d) => Math.max(d.booked, d.capacity))));
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const y = (m: number) => `${(m / top) * 100}%`;
  const every = data.length > 30 ? 4 : data.length > 14 ? 2 : 1;
  const hasStates = data.some((d) => d.state === 'under' || d.state === 'over');
  const columns: Series[] = [
    { key: 'booked', label: t('kpi.booked') },
    { key: 'billable', label: t('kpi.billable') },
    ...(showCapacity ? [{ key: 'capacity', label: t('kpi.capacity') }] : []),
    ...(hasStates ? [{ key: 'state', label: t('kpi.weekState') }] : []),
  ];
  const stateText = (d: WeekDatum) =>
    d.state === 'under' ? t('kpi.under', { hours: h(d.capacity - (d.comp ?? 0) - d.booked) }) : d.state === 'over' ? t('kpi.over', { hours: h(d.booked - d.capacity) }) : '';
  return (
    <Frame
      legend={
        <Legend
          items={[
            { kind: 'strong', label: t('kpi.billable') },
            { kind: 'soft', label: t('kpi.otherHours') },
            ...(showCapacity ? [{ kind: 'line' as const, label: t('kpi.capacity') }] : []),
            ...(hasStates
              ? [
                  { kind: 'over' as const, label: t('kpi.overLegend') },
                  { kind: 'under' as const, label: t('kpi.underLegend') },
                ]
              : []),
          ]}
        />
      }
      table={
        <DataTable
          head={head ?? t('common.week')}
          columns={columns}
          rows={data.map((d) => ({ label: d.label, values: { booked: d.booked, billable: d.billable, capacity: d.capacity, state: stateText(d) } }))}
        />
      }
    >
      <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-x-2">
        <div className="relative" style={{ height }}>
          {ticks.map((tk) => (
            <span key={tk} className="num absolute right-0 translate-y-1/2 text-[11px] leading-none text-graphite" style={{ bottom: y(tk) }}>
              {h(tk, 0)}
              {tk === top ? ' h' : ''}
            </span>
          ))}
        </div>
        <div className="relative" style={{ height }}>
          {ticks.map((tk) => (
            <div key={tk} className={clsx('absolute inset-x-0 h-px', tk === 0 ? 'bg-line-strong' : 'bg-line')} style={{ bottom: y(tk) }} />
          ))}
          <div className="absolute inset-0 flex">
            {data.map((d, i) => {
              const other = Math.max(0, d.booked - d.billable);
              const billable = Math.min(d.billable, d.booked);
              return (
                <div
                  key={d.label}
                  tabIndex={0}
                  role="img"
                  aria-label={`${d.label}: ${t('kpi.booked')} ${h(d.booked)} h, ${t('kpi.billable')} ${h(d.billable)} h${showCapacity ? `, ${t('kpi.capacity')} ${h(d.capacity)} h` : ''}. ${stateText(d)}`}
                  onPointerEnter={() => setActive(i)}
                  onPointerLeave={() => setActive((a) => (a === i ? null : a))}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive((a) => (a === i ? null : a))}
                  className={clsx('relative flex min-w-0 flex-1 justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-accent', active === i ? 'bg-sheet-2' : d.current && 'bg-accent-soft/40')}
                >
                  {d.booked > 0 && (
                    <div className="absolute bottom-0 flex flex-col-reverse gap-0.5" style={{ height: y(d.booked), width: 'min(24px, calc(100% - 4px))' }}>
                      {billable > 0 && <div className={clsx('bg-viz-strong', other === 0 && 'rounded-t')} style={{ flex: `${billable} 1 0` }} />}
                      {other > 0 && <div className="rounded-t bg-viz-soft" style={{ flex: `${other} 1 0` }} />}
                    </div>
                  )}
                  {d.capacity > 0 && <div className="absolute h-0.5 rounded bg-ink" style={{ bottom: `calc(${y(d.capacity)} - 1px)`, width: 'min(36px, calc(100% - 2px))' }} />}
                  {active === i && (
                    <div
                      className={clsx(
                        'pointer-events-none absolute bottom-full z-10 mb-1.5 flex w-max flex-col gap-0.5 rounded-md border border-line bg-sheet px-2.5 py-1.5 text-xs shadow-md',
                        i >= data.length / 2 ? 'right-0' : 'left-0',
                      )}
                    >
                      <div className="font-medium text-graphite">{d.label}</div>
                      <TooltipRow kind="soft" value={d.booked} label={t('kpi.booked')} />
                      <TooltipRow kind="strong" value={d.billable} label={t('kpi.billable')} />
                      {showCapacity && <TooltipRow kind="line" value={d.capacity} label={t('kpi.capacity')} />}
                      {d.state === 'under' && <div className="flex items-center gap-1 font-medium text-bad"><ChevronDown className="size-3.5" />{stateText(d)}</div>}
                      {d.state === 'over' && <div className="flex items-center gap-1 font-medium text-warn"><ChevronUp className="size-3.5" />{stateText(d)}</div>}
                      {d.note && <div className="text-graphite">{d.note}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div />
        <div className="flex">
          {data.map((d, i) => (
            <div key={d.label} className={clsx('num flex min-w-0 flex-1 flex-col items-center pt-1.5 text-[11px]', d.current ? 'font-semibold text-accent' : 'text-graphite')}>
              <span>{i % every === 0 || d.current ? d.axis : ''}</span>
              {hasStates && (
                <span className="h-3.5">
                  {d.state === 'over' && <ChevronUp className="size-3.5 text-warn" aria-hidden />}
                  {d.state === 'under' && <ChevronDown className="size-3.5 text-bad" aria-hidden />}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

export interface StackSeries {
  key: string;
  label: string;
  kind: Exclude<Kind, 'line'>;
}

export interface StackRow {
  id?: string;
  label: string;
  /** Second line under the label (muted). */
  sub?: string;
  values: Record<string, number>;
  /** Text after the bar (default: total hours). */
  end?: ReactNode;
  onClick?: () => void;
}

/** Horizontal stacked bars (one blue ramp); total at the bar end. */
export function StackBars({ rows, series, head, labelWidth = '14rem' }: { rows: StackRow[]; series: StackSeries[]; head: ReactNode; labelWidth?: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState<number | null>(null);
  const total = (r: StackRow) => series.reduce((s, x) => s + (r.values[x.key] ?? 0), 0);
  const max = Math.max(1, ...rows.map(total));
  // room for the value at the end of the bar ("2.213,8 · 53 %" needs more than a plain number)
  const reserve = rows.some((r) => r.end) ? '7.5rem' : '4.5rem';
  return (
    <Frame
      legend={<Legend items={series.map((s) => ({ kind: s.kind, label: s.label }))} />}
      table={
        <DataTable
          head={head}
          columns={[...series.map((s) => ({ key: s.key, label: s.label })), { key: '__total', label: t('common.total') }]}
          rows={rows.map((r) => ({ label: r.label, values: { ...r.values, __total: total(r) } }))}
        />
      }
    >
      <ul className="flex flex-col gap-1">
        {rows.map((r, i) => {
          const sum = total(r);
          const parts = series.filter((s) => (r.values[s.key] ?? 0) > 0);
          const content = (
            <>
              <span className="min-w-0">
                <span className="block truncate">{r.label}</span>
                {r.sub && <span className="block truncate text-xs text-graphite">{r.sub}</span>}
              </span>
              <span className="flex min-w-0 items-center gap-2">
                <span className="flex h-3 shrink-0 gap-0.5" style={{ width: `calc((100% - ${reserve}) * ${sum / max})` }}>
                  {parts.map((s, n) => (
                    <span key={s.key} className={clsx(FILL[s.kind], n === parts.length - 1 && 'rounded-r')} style={{ flex: `${r.values[s.key]} 1 0` }} />
                  ))}
                </span>
                <span className="num text-xs whitespace-nowrap text-graphite">{r.end ?? h(sum, 1)}</span>
              </span>
            </>
          );
          const cls = clsx(
            'relative grid w-full items-center gap-3 rounded-sm px-1 py-1 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent',
            active === i && 'bg-sheet-2',
          );
          const events = {
            onPointerEnter: () => setActive(i),
            onPointerLeave: () => setActive((a) => (a === i ? null : a)),
            onFocus: () => setActive(i),
            onBlur: () => setActive((a) => (a === i ? null : a)),
          };
          const label = `${r.label}: ${series.map((s) => `${s.label} ${h(r.values[s.key] ?? 0)} h`).join(', ')}`;
          return (
            <li key={r.id ?? r.label} className="relative">
              {r.onClick ? (
                <button type="button" aria-label={label} className={clsx(cls, 'cursor-pointer')} style={{ gridTemplateColumns: `minmax(0,${labelWidth}) minmax(0,1fr)` }} onClick={r.onClick} {...events}>
                  {content}
                </button>
              ) : (
                <div tabIndex={0} aria-label={label} className={cls} style={{ gridTemplateColumns: `minmax(0,${labelWidth}) minmax(0,1fr)` }} {...events}>
                  {content}
                </div>
              )}
              {active === i && (
                <div className="pointer-events-none absolute right-1 bottom-full z-10 flex w-max flex-col gap-0.5 rounded-md border border-line bg-sheet px-2.5 py-1.5 text-xs shadow-md">
                  <div className="font-medium text-graphite">{r.label}</div>
                  {series.map((s) => (
                    <TooltipRow key={s.key} kind={s.kind} value={r.values[s.key] ?? 0} label={s.label} />
                  ))}
                  <div className="num border-t border-line pt-0.5 text-graphite">
                    {t('common.total')} {h(sum)} h
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Frame>
  );
}

export interface BarRow {
  label: string;
  /** Total booked minutes. */
  value: number;
  /** Billable part of value. */
  billable: number;
}

/** Horizontal bars: billable part (strong) and the rest (soft). */
export function HBars({ rows, head }: { rows: BarRow[]; head: ReactNode }) {
  const { t } = useTranslation();
  return (
    <StackBars
      head={head}
      labelWidth="12rem"
      series={[
        { key: 'billable', label: t('kpi.billable'), kind: 'strong' },
        { key: 'other', label: t('kpi.otherHours'), kind: 'soft' },
      ]}
      rows={rows.map((r) => ({ label: r.label, values: { billable: Math.min(r.billable, r.value), other: Math.max(0, r.value - r.billable) } }))}
    />
  );
}

/** Plan against actual as two thin bars (plan soft, actual strong) with the factor; used in task lists. */
export function PlanBar({ planned, booked, className }: { planned: number; booked: number; className?: string }) {
  const max = Math.max(planned, booked, 1);
  const over = booked > planned;
  return (
    <span className={clsx('inline-flex w-24 flex-col gap-0.5', className)} aria-hidden>
      <span className="h-1 rounded-r bg-viz-soft" style={{ width: `${(planned / max) * 100}%` }} />
      <span className={clsx('h-1 rounded-r', over ? 'bg-warn' : 'bg-viz-strong')} style={{ width: `${(booked / max) * 100}%` }} />
    </span>
  );
}
